import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Inbox } from 'lucide-react';
import { AppLayout } from '@/components/layout/app-layout';
import { formatoCantidad } from '@/lib/utils/formato-precio';
import { ProposalsInboxTab } from '@/features/smart-pricing/components/proposals-inbox-tab';
import { VueltaSheet } from '@/features/smart-pricing/components/vuelta-sheet';
import { priceProposalsApi } from '@/features/smart-pricing/services/price-proposals-api';
import type { FiltroBandeja } from '@/features/smart-pricing/types/price-proposals.types';
import { MarcoCompite } from '../ui/marco-compite';
import { EncabezadoCompite } from '../ui/encabezado-compite';
import { AvisoCambiosNuevos } from '../components/aviso-cambios-nuevos';
import { avisarPendientesDeDecidir } from '../hooks/use-pendientes-de-decidir';
import {
  RUTAS_COMPITE,
  useOrquestacionCompite,
  usePermisosDeCompite,
  type EstadoDeLlegada,
} from '../hooks/use-orquestacion-compite';

/**
 * Compite › Decisiones › Revisar precios (antes «Por decidir»). La bandeja donde
 * se aprueba o se descarta cada precio propuesto, con el por qué de cada uno.
 *
 * Se llega de dos formas: desde Decisiones (ya filtrado por la estrategia o por
 * lo que se tocó) o directo por el menú. La estrategia y el producto viajan en la
 * URL (`?estrategia=`, `?producto=`); el resto del filtro, como estado de la
 * navegación, que llega una vez y no se queda pegado.
 */
export default function RevisarPreciosPage() {
  const { veEstrategias, veReglas } = usePermisosDeCompite();
  const o = useOrquestacionCompite();
  const [params] = useSearchParams();
  const location = useLocation();
  const [pendientes, setPendientes] = useState<number | null>(null);
  // A la derecha del encabezado: los cuatro estados de la bandeja (como la maqueta,
  // la tabla queda sola en su tarjeta).
  const [destinoDelEstado, setDestinoDelEstado] = useState<HTMLDivElement | null>(null);

  const estrategia = params.get('estrategia');
  const producto = params.get('producto');
  const llegada = (location.state as EstadoDeLlegada | null)?.filtro ?? null;
  const filtroInicial: FiltroBandeja | null = producto
    ? { ...(llegada ?? {}), productId: producto }
    : llegada;

  // El número del título («Revisar precios · 12»). Si no llega, sin número.
  useEffect(() => {
    let vivo = true;
    priceProposalsApi
      .contar()
      .then((c) => {
        if (vivo) setPendientes(c.PENDING ?? 0);
      })
      .catch(() => {
        if (vivo) setPendientes(null);
      });
    return () => {
      vivo = false;
    };
  }, [o.refreshKey]);

  if (!veEstrategias) {
    return (
      <Navigate to={veReglas ? `${RUTAS_COMPITE.estrategia}?tab=reglas` : '/dashboard'} replace />
    );
  }

  return (
    <AppLayout>
      <MarcoCompite>
        <EncabezadoCompite
          seccion="Decisiones"
          icono={Inbox}
          sinPildora
          arriba={
            <Link
              to={RUTAS_COMPITE.decisiones}
              className="inline-flex items-center gap-1.5 rounded-sm text-sm font-semibold text-primary underline-offset-4 transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Decisiones de hoy
            </Link>
          }
          titulo={
            <>
              Revisar precios
              {pendientes != null && pendientes > 0 && (
                <span className="text-muted-foreground"> · {formatoCantidad(pendientes)}</span>
              )}
            </>
          }
          subtitulo="Cada precio viene de una estrategia y respeta tus reglas. Aprobalo o descartalo."
          acciones={<div ref={setDestinoDelEstado} />}
        />
        {o.hayCambiosNuevos && <AvisoCambiosNuevos onRecargar={o.recargar} />}
        <ProposalsInboxTab
          // La clave reinicia la bandeja cuando se llega con otro filtro desde Decisiones.
          key={`${estrategia ?? 'todas'}-${producto ?? ''}-${location.key}`}
          filtroEstrategiaInicial={estrategia}
          filtroInicial={filtroInicial}
          onAbrirVuelta={(id) => o.abrirVuelta(id)}
          refreshKey={o.refreshKey}
          destinoDelEstado={destinoDelEstado}
          // Lo que la bandeja cuenta después de cada decisión: el título y el
          // menú bajan en el momento (antes quedaban en el número de la entrada).
          onConteo={(c) => {
            setPendientes(c.PENDING ?? 0);
            avisarPendientesDeDecidir(c.PENDING ?? 0);
          }}
        />
      </MarcoCompite>
      <VueltaSheet
        strategyApplicationId={o.vuelta}
        onClose={() => o.abrirVuelta(null)}
        onIrA={o.irA}
        onResuelto={o.alResolver}
      />
    </AppLayout>
  );
}
